import {
  client,
  ProgramPayload,
  CreateProgramRequest,
  ProgramVideoInput,
} from "../../../lib/api";

// Mirror your exact object type shape here
export type VideoResultType = {
  type: "video_result";
  url: string;
  title: string;
  description: string;
  age?: string;
  pageAge?: string;
  video?: {
    duration?: string;
    views?: number;
    creator?: string;
    publisher?: string;
  };
  metaUrl?: {
    hostname?: string;
  };
  thumbnail?: {
    src?: string;
    original?: string;
  };
  embedUrl?: string;
};

export type VideoSelectionMap = {
  [exerciseIndex: number]: {
    videoData: VideoResultType;
    savedExerciseId?: string; // Stored after creating the embedded resource
  };
};

export const submitProgramPipeline = async ({
  payload,
  videoSelections,
}: {
  payload: ProgramPayload;
  videoSelections: VideoSelectionMap;
}) => {
  const updatedSelections = { ...videoSelections };

  // 1. Loop through tracking maps and create internal video records first
  for (const index of Object.keys(updatedSelections)) {
    const idx = Number(index);
    const selection = updatedSelections[idx];
    const exercise = payload.exercises[idx];

    if (selection?.videoData?.url) {
      // Create external video source records sequentially
      const res = await client.exercises.postExercisesEmbed({
        requestBody: {
          url: selection.videoData.url,
          title: selection.videoData.title || exercise.title,
          description: selection.videoData.description || exercise.description,
        },
      });

      if (res?.exercise.id) {
        updatedSelections[idx].savedExerciseId = res.exercise.id;
      }
    }
  }

  // 2. Map payload items using newly created exercise IDs
  const programVideos: ProgramVideoInput[] = payload.exercises.map(
    (exercise, index) => {
      const selection = updatedSelections[index];

      return {
        exerciseId: selection?.savedExerciseId || "",
        role: exercise.role,
        sets: (exercise.dosage as any)?.sets ?? null,
        reps: (exercise.dosage as any)?.reps ?? null,
        restSeconds: (exercise.dosage as any)?.restSeconds ?? null,
        tempo: (exercise.dosage as any)?.tempo ?? null,
        notes: exercise.description || null,
      };
    },
  );

  // 3. Dispatch the parent program post body
  return client.programs.postPrograms({
    requestBody: {
      targetIds: payload.targetIds,

      title: payload.title,
      description: payload.description,
      notificationActive: true,
      notificationTime: "09:00",
      notificationDays: payload.notificationDays || [],
      startDate: new Date().toISOString().split("T")[0],
      endDate: calculateEndDate(
        payload.numberOfSessions || 1,
        payload.notificationDays || [1],
        new Date(),
      )
        .toISOString()
        .split("T")[0],
      programVideos,
    } satisfies CreateProgramRequest,
  });
};

export function calculateEndDate(
  totalSessions: number,
  workoutDays: number[], // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  startDate: Date,
): Date {
  if (totalSessions <= 0 || workoutDays.length === 0) {
    throw new Error(
      "Sessions must be > 0 and at least one workout day must be provided.",
    );
  }

  // Sort workout days ascending to ensure chronological order
  const targetDays = [...workoutDays].sort((a, b) => a - b);
  let currentDate = new Date(startDate.getTime());
  let remainingSessions = totalSessions;

  // Check if start date lands on a workout day to count as the first session
  if (targetDays.includes(currentDate.getDay())) {
    remainingSessions--;
  }

  // Loop day-by-day until all sessions are completed
  while (remainingSessions > 0) {
    currentDate.setDate(currentDate.getDate() + 1);
    if (targetDays.includes(currentDate.getDay())) {
      remainingSessions--;
    }
  }

  return currentDate;
}
